import {
  AppShell,
  Avatar,
  avatarColorFor,
  Badge,
  BottomNav,
  Button,
  BottomNavItem,
  Fab,
  IconButton,
  Menu,
  type MenuItem,
  SidebarNav,
  SidebarNavItem,
  Stack,
  Text,
  TopBar,
  useTheme,
} from '@giftgroup/ui';
import { Bell, Calendar, Home, Lightbulb, Plus, ShoppingBag, Star, Users } from 'lucide-react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { commonCopy } from '../copy/common';
import { PushPromptSheet } from '../features/notifications/PushPromptSheet';
import { calendarCopy } from '../copy/calendar';
import { signOut } from '../lib/auth';
import { clearQueryCache } from '../lib/queryClient';
import { useActiveFamily, useMe, useSwitchFamily } from '../lib/session';
import { OfflineBanner } from './OfflineBanner';
import { RouteFocus } from './RouteFocus';
import s from './shell.module.css';

/**
 * The bottom bar holds five: Wishlist took Family's tab (doc 15), and Family moved to the
 * avatar menu. The desktop sidebar has room for all six.
 */
const NAV = [
  { to: '/home', icon: Home, label: commonCopy.nav.home },
  { to: '/calendar', icon: Calendar, label: commonCopy.nav.calendar },
  { to: '/wishlist', icon: Star, label: commonCopy.nav.wishlist },
  { to: '/shopping', icon: ShoppingBag, label: commonCopy.nav.shopping },
  { to: '/ideas', icon: Lightbulb, label: commonCopy.nav.ideas },
] as const;
const SIDEBAR_NAV = [...NAV, { to: '/family', icon: Users, label: commonCopy.nav.family }] as const;

const THEME_ORDER = { light: 'dark', dark: 'system', system: 'light' } as const;

/**
 * The signed-in frame (doc 05): top bar and bottom nav on phones, sidebar from `lg`.
 * Every app route renders inside it.
 */
/** The "+" appears where there's something to add (doc 05). */
const FAB_ROUTES = ['/home', '/calendar', '/ideas'];

export function AppLayout() {
  const { data: me } = useMe();
  const family = useActiveFamily(me);
  const navigate = useNavigate();
  const location = useLocation();
  const switchFamily = useSwitchFamily();
  const { preference, setPreference } = useTheme();

  const unread = me?.unreadNotifications ?? 0;
  const toBuy = me?.shoppingToBuyCount ?? 0;
  const shoppingBadge = (to: string) =>
    to === '/shopping' && toBuy > 0
      ? { badge: toBuy, badgeLabel: commonCopy.nav.shoppingBadge(toBuy) }
      : {};

  async function handleSignOut() {
    await signOut();
    // A shared phone must not keep the last person's lists in memory (doc 03).
    clearQueryCache();
    navigate('/welcome', { replace: true });
  }

  /** What "+" offers: the Fab on phones, the sidebar's New button on desktop (doc 05). */
  const createItems: MenuItem[] = [
    {
      key: 'gift-list',
      label: calendarCopy.fab.newGiftList,
      onSelect: () => navigate('/gift-lists/new'),
    },
    {
      key: 'wishlist',
      label: calendarCopy.fab.addToWishlist,
      onSelect: () => navigate('/wishlist'),
    },
    {
      key: 'idea',
      label: calendarCopy.fab.saveIdea,
      onSelect: () => navigate('/ideas'),
    },
    {
      key: 'date',
      label: calendarCopy.fab.trackDate,
      onSelect: () => navigate('/dates/new'),
    },
    {
      key: 'person',
      label: calendarCopy.fab.addPerson,
      onSelect: () => navigate('/family/people/new'),
    },
  ];

  const accountMenu = (
    <Menu
      label={commonCopy.nav.account}
      trigger={
        <button type="button" className={s.avatarButton} aria-label={commonCopy.nav.account}>
          <Avatar
            name={me?.user.name ?? '?'}
            color={avatarColorFor(me?.user.id ?? '')}
            size="sm"
            src={me?.user.image}
            decorative
          />
        </button>
      }
      items={[
        {
          key: 'family',
          label: commonCopy.account.family,
          onSelect: () => navigate('/family'),
        },
        {
          key: 'settings',
          label: commonCopy.account.settings,
          onSelect: () => navigate('/settings'),
        },
        // Other families first-class in the menu: switching is a common mobile action.
        ...(me?.families ?? [])
          .filter((f) => f.id !== family?.id)
          .map((f) => ({
            key: `family-${f.id}`,
            label: `${commonCopy.account.switchFamily}: ${f.name}`,
            onSelect: () => switchFamily.mutate(f.id),
          })),
        {
          key: 'join',
          label: commonCopy.account.joinAnother,
          onSelect: () => navigate('/start/join'),
        },
        {
          key: 'theme',
          label: `${commonCopy.account.theme}: ${preference}`,
          onSelect: () => setPreference(THEME_ORDER[preference]),
        },
        {
          key: 'sign-out',
          label: commonCopy.account.signOut,
          onSelect: () => void handleSignOut(),
        },
      ]}
    />
  );

  return (
    <AppShell
      beforeTopBar={
        <a href="#main" className={s.skipLink}>
          {commonCopy.nav.skipToContent}
        </a>
      }
      topBar={
        <TopBar
          title={family?.name ?? commonCopy.appName}
          actions={
            <>
              <span className={s.bell}>
                <IconButton
                  icon={Bell}
                  label={commonCopy.nav.notifications}
                  variant="ghost"
                  onClick={() => navigate('/notifications')}
                />
                {unread > 0 ? (
                  <Badge count={unread} label={`${unread} unread`} className={s.bellBadge} />
                ) : null}
              </span>
              {accountMenu}
            </>
          }
        />
      }
      bottomNav={
        <BottomNav label={commonCopy.nav.main}>
          {NAV.map((item) => (
            <BottomNavItem
              key={item.to}
              icon={item.icon}
              label={item.label}
              link={<NavLink to={item.to} />}
              {...shoppingBadge(item.to)}
            />
          ))}
        </BottomNav>
      }
      sidebar={
        <SidebarNav
          label={commonCopy.nav.main}
          header={
            <Stack gap={4}>
              <Text size="bodyLg" weight="bold">
                {family?.name ?? commonCopy.appName}
              </Text>
              {/* The Fab hides from `lg`, so desktop needs its own way to add things. */}
              <Menu
                label={calendarCopy.fab.label}
                trigger={
                  <Button iconStart={Plus} fullWidth>
                    {calendarCopy.fab.newButton}
                  </Button>
                }
                items={createItems}
              />
            </Stack>
          }
          footer={accountMenu}
        >
          {SIDEBAR_NAV.map((item) => (
            <SidebarNavItem
              key={item.to}
              icon={item.icon}
              label={item.label}
              link={<NavLink to={item.to} />}
              {...shoppingBadge(item.to)}
            />
          ))}
        </SidebarNav>
      }
    >
      <RouteFocus />
      <OfflineBanner />
      <Outlet />
      {/* Asks about notifications after a moment worth celebrating (doc 11 › UX). */}
      <PushPromptSheet />

      {FAB_ROUTES.includes(location.pathname) ? (
        <Menu
          label={calendarCopy.fab.label}
          /*
           * The Fab itself, not a span around it: `asChild` clones the trigger's props
           * onto whatever it's given, and on a span that means `aria-haspopup` on an
           * element that can't have it (an axe violation), plus a menu that only opened
           * because the click happened to bubble.
           */
          trigger={<Fab icon={Plus} label={calendarCopy.fab.label} />}
          items={createItems}
        />
      ) : null}
    </AppShell>
  );
}
